import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

// ════════════════════════════════════════════════════════════════════════════
// Dev-preview FSM store — P4 of dev-preview-button.md (#1403).
//
// Origin-keyed Zustand store for the dev-preview HMR-from-worktree button,
// applying the workspace Frontend FSM pattern
// (server-config/docs/frontend-fsm-pattern.md):
//   1. Explicit FSM        — the `phase` union + the exported TRANSITIONS table.
//   2. Origin-keyed store  — Map<sessionId, DevPreviewState> with persist. The
//                            origin is the CHAT SESSION (one session ⇒ at most one
//                            active worktree, per the operator's invariant §3.2).
//   3. Server-authoritative — reconcile(sid, status) is the only truth signal for
//                            "is the sidecar serving THIS session's worktree".
//                            GET /dev-preview/status is the authoritative sensor;
//                            no frontend timeout guesses, no synthesized claims.
//   4. Atomic transitions  — one user intent = one named action (requestBind,
//                            bindSucceeded, bindFailed), never scattered setters.
//   5. Setpoint evals      — setpoint-evals/dev-preview/ drives THIS store
//                            (simulator + SE-DP-* scenarios).
//
// The dev-preview "wire" is REST, not Socket.IO (unlike sshTerminalStore): the
// two server events are the GET /dev-preview/status response (→ reconcile) and
// the POST /dev-preview/bind response (→ bindSucceeded / bindFailed). The
// DevPreviewButton component is the wire adapter (it calls authFetch); this store
// only owns state, exactly as the pattern prescribes.
//
// Singleton-binding caveat (§3.1): the host sidecar binding is GLOBAL — one
// worktree previewable at a time. Each session's entry records the (global)
// sidecar's bound worktree/branch as observed at its last reconcile, so the
// button can show "currently serving <branch>" and never silently serve the wrong
// thing. `phase === 'serving'` means "the sidecar is serving THIS session's
// detected worktree"; if it's serving a DIFFERENT worktree the phase is `idle`
// (a rebind is needed) while boundBranch still surfaces what it's serving now.
// ════════════════════════════════════════════════════════════════════════════

// ─── State types ───────────────────────────────────────────────────────────────

/**
 * FSM phase for a single session's dev-preview button.
 *
 *   idle      not serving this session's worktree (never bound, sidecar down, or
 *             bound to a DIFFERENT worktree). Tapping binds + opens.
 *   binding   POST /dev-preview/bind in flight, awaiting the response.
 *   serving   the sidecar is serving THIS session's detected worktree; tapping
 *             opens dev-voice.akrsmv.net.
 *   error     the last bind attempt failed (no worktree detected, trigger write
 *             failed, …). lastError carries the message; awaiting retry / dismiss.
 *
 * Full transition table is exported as `TRANSITIONS` below (R-A1).
 */
export type DevPreviewPhase = 'idle' | 'binding' | 'serving' | 'error';

/**
 * PR8 (agent-visual-resource-leasing §14e): provenance of the detected/bound worktree.
 *   'session'  — this session's tool-detected worktree (trusted).
 *   'fallback' — degraded to the newest worktree on disk; may be an UNRELATED branch. The badge
 *                makes this state visibly distinct so the operator never silently previews the
 *                wrong branch.
 *   'none'     — nothing resolved.
 */
export type WorktreeSource = 'session' | 'fallback' | 'none';

export interface DevPreviewState {
  phase: DevPreviewPhase;
  /** This session's active worktree (backend session→worktree detection §3.2). */
  detectedWorktree: string | null;
  detectedBranch: string | null;
  /** PR8: where detectedWorktree came from — drives the fallback badge. */
  worktreeSource: WorktreeSource;
  /** The GLOBAL sidecar's currently-bound worktree/branch (observed at reconcile).
   *  May differ from detectedWorktree — that's the singleton-binding caveat. */
  boundRoot: string | null;
  boundBranch: string | null;
  /** Raw sidecar state string from the host status file ('serving' | … | null). */
  sidecarState: string | null;
  /** dev-voice URL to open (from the server — never hardcoded client-side). */
  devVoiceUrl: string | null;
  /** Error message when phase === 'error'. Cleared on requestBind / dismissError. */
  lastError: string | null;
}

export interface DevPreviewStoreShape {
  /** Origin-keyed: one entry per chat session. */
  sessions: Map<string, DevPreviewState>;
}

/**
 * Server-authoritative reconcile payload — mirrors the backend
 * `DevPreviewStatus` interface (dev-preview.service.ts). SE-DP-WIRE-FAITHFUL
 * (R-A5 adapted to REST) asserts this shape stays in lockstep with the backend
 * interface so the wire can't drift ahead of the simulator/reconciler.
 */
export interface DevPreviewStatusWire {
  detectedWorktree: string | null;
  detectedBranch: string | null;
  // PR8: worktree provenance. Top-level so the wire-drift SE (SE-DP-12) sees it against the
  // backend DevPreviewStatus.worktreeSource field-for-field.
  worktreeSource: WorktreeSource;
  sidecar: { state: string; boundRoot: string | null; boundBranch: string | null } | null;
  devVoiceUrl: string;
  // resource-lease §5b: who holds the shared HMR sidecar. Mirrors backend DevPreviewLeaseInfo.
  // The reconciler ignores it today (no UX yet); the §5d status surface consumes it. Kept as a
  // single-line inline object (like `sidecar`) so the wire-drift SE sees only `lease` at top level.
  lease: { held: boolean; mine: boolean; heldBy: string | null; since: number | null; expiresAt: number | null; waiters: number };
}

/** Result of POST /dev-preview/bind (backend DevPreviewService.bind). */
export interface DevPreviewBindResult {
  ok: boolean;
  worktree?: string;
  branch?: string | null;
  // PR8: provenance of the bound worktree — mirrors backend DevPreviewBindResult.worktreeSource.
  worktreeSource?: WorktreeSource;
  devVoiceUrl?: string;
  error?: string;
  // resource-lease §5b: set when the sidecar is held by ANOTHER session (refused, not clobbered).
  busy?: boolean;
  heldBy?: string;
  expiresAt?: number;
  queuePos?: number;
}

// ─── Default state factory ──────────────────────────────────────────────────────

function defaultDevPreviewState(): DevPreviewState {
  return {
    phase: 'idle',
    detectedWorktree: null,
    detectedBranch: null,
    worktreeSource: 'none',
    boundRoot: null,
    boundBranch: null,
    sidecarState: null,
    devVoiceUrl: null,
    lastError: null,
  };
}

// ─── Transition table (R-A1) ────────────────────────────────────────────────────
//
// The canonical FSM, mirrored from dev-preview-button.md §5B (P4 row). `run-all.sh`
// (SE-DP-FSM-COVERAGE) validates that every distinct `trigger` here is covered by a
// simulator scenario's `// SE-COVERS:` marker — adding a transition MUST add a
// covering scenario. Each `trigger` is a NAMED store action or a NAMED reconcile
// outcome — never an "anything else" path (R-A2). Self-loops that don't change the
// phase (e.g. reconcile:idle on an already-idle entry) reuse the same trigger and
// so add no new coverage requirement.

export const TRANSITIONS: Array<{ from: DevPreviewPhase; to: DevPreviewPhase; trigger: string }> = [
  { from: 'idle', to: 'binding', trigger: 'requestBind' },
  { from: 'serving', to: 'binding', trigger: 'requestBind' },
  { from: 'error', to: 'binding', trigger: 'requestBind' },
  { from: 'binding', to: 'serving', trigger: 'bindSucceeded' },
  { from: 'binding', to: 'error', trigger: 'bindFailed' },
  { from: 'idle', to: 'serving', trigger: 'reconcile:serving' },
  { from: 'serving', to: 'idle', trigger: 'reconcile:idle' },
  { from: 'error', to: 'idle', trigger: 'dismissError' },
];

// ─── Map serializer ─────────────────────────────────────────────────────────────
// JSON.stringify/parse do not round-trip Map natively. These replacer/reviver
// functions let the persist middleware serialize the `sessions` Map. Mirrors
// sshTerminalStore.ts / composerStore.ts; covered by SE-DP-STORE-SHAPE.

function mapReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    return { __type: 'Map', entries: [...value.entries()] };
  }
  return value;
}

function mapReviver(_key: string, value: unknown): unknown {
  if (
    value !== null &&
    typeof value === 'object' &&
    (value as { __type?: string }).__type === 'Map'
  ) {
    return new Map((value as { entries: [unknown, unknown][] }).entries);
  }
  return value;
}

const mapAwareStorage = createJSONStorage(() => localStorage, {
  replacer: mapReplacer,
  reviver: mapReviver,
});

// ─── Store creation ─────────────────────────────────────────────────────────────

export const useDevPreviewStore = create<DevPreviewStoreShape>()(
  persist(
    (): DevPreviewStoreShape => ({
      sessions: new Map<string, DevPreviewState>(),
    }),
    {
      name: 'va-dev-preview-v1',
      storage: mapAwareStorage,
      // Persist METADATA ONLY — the last-known detected/bound worktree so the
      // button can show "last bound <branch>" instantly on open, before the
      // reconcile fetch returns. `phase` is reset to 'idle' and `lastError`
      // cleared: on reload there is NO live confirmation that the sidecar is
      // still serving, so we never trust a persisted live phase (pattern rule 3 —
      // never synthesize server state). The next GET /dev-preview/status
      // reconcile is authoritative.
      partialize: (state) => ({
        sessions: new Map(
          [...state.sessions.entries()].map(([id, s]) => [
            id,
            { ...s, phase: 'idle' as DevPreviewPhase, lastError: null },
          ]),
        ),
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const next = new Map<string, DevPreviewState>();
        for (const [id, s] of state.sessions.entries()) {
          next.set(id, { ...s, phase: 'idle', lastError: null });
        }
        useDevPreviewStore.setState({ sessions: next });
      },
    },
  ),
);

// ─── Internal helper ─────────────────────────────────────────────────────────────

/** Upsert a session entry through a transform, returning a fresh Map (immutable). */
function patchSession(
  sid: string,
  patch: (cur: DevPreviewState) => DevPreviewState,
): void {
  useDevPreviewStore.setState((s) => {
    const sessions = new Map(s.sessions);
    const cur = sessions.get(sid) ?? defaultDevPreviewState();
    sessions.set(sid, patch(cur));
    return { sessions };
  });
}

// ─── Actions (one named action per transition / intent — R-A2/R-A4) ─────────────

/**
 * idle | serving | error → binding. User tapped "bind this session's worktree".
 * The DevPreviewButton wire then POSTs /dev-preview/bind; bindSucceeded /
 * bindFailed resolve it. No-op while already binding (debounces double-taps).
 */
export function requestBind(sid: string): void {
  patchSession(sid, (cur) =>
    cur.phase === 'binding' ? cur : { ...cur, phase: 'binding', lastError: null },
  );
}

/**
 * binding → serving (POST /dev-preview/bind ok:true). ATOMIC: records the bound
 * worktree/branch + devVoiceUrl + sidecarState in one action (R-A4).
 */
export function bindSucceeded(
  sid: string,
  result: { worktree: string; branch: string | null; devVoiceUrl: string; worktreeSource?: WorktreeSource },
): void {
  patchSession(sid, (cur) => ({
    ...cur,
    phase: 'serving',
    boundRoot: result.worktree,
    boundBranch: result.branch,
    // PR8: a fallback-BOUND state must keep its badge post-bind (default to prior value if the
    // bind response predates the field).
    worktreeSource: result.worktreeSource ?? cur.worktreeSource,
    sidecarState: 'serving',
    devVoiceUrl: result.devVoiceUrl,
    lastError: null,
  }));
}

/** binding → error (POST /dev-preview/bind ok:false, or network failure). */
export function bindFailed(sid: string, message: string): void {
  patchSession(sid, (cur) => ({ ...cur, phase: 'error', lastError: message }));
}

/**
 * Server-authoritative reconcile (GET /dev-preview/status). The SINGLE truth
 * signal for whether the sidecar is serving THIS session's worktree (pattern
 * rule 3). Always refreshes the data fields (detected + bound + sidecar +
 * devVoiceUrl) from the status. Phase resolution:
 *   - in-flight `binding` → left untouched (don't clobber a pending bind; the
 *     bind response, not a stale status poll, owns that transition).
 *   - `error`            → left in error (only requestBind / dismissError clear it).
 *   - otherwise          → `serving` iff the sidecar is serving the detected
 *                          worktree (reconcile:serving), else `idle`
 *                          (reconcile:idle — sidecar down OR serving a different
 *                          worktree, which needs a rebind).
 * One atomic action covering both `*→serving` and `*→idle`.
 */
export function reconcile(sid: string, status: DevPreviewStatusWire): void {
  patchSession(sid, (cur) => {
    const sidecar = status.sidecar;
    const servingThis =
      !!sidecar &&
      sidecar.state === 'serving' &&
      !!status.detectedWorktree &&
      sidecar.boundRoot === status.detectedWorktree;

    const base: DevPreviewState = {
      ...cur,
      detectedWorktree: status.detectedWorktree,
      detectedBranch: status.detectedBranch,
      worktreeSource: status.worktreeSource ?? 'none',
      boundRoot: sidecar?.boundRoot ?? null,
      boundBranch: sidecar?.boundBranch ?? null,
      sidecarState: sidecar?.state ?? null,
      devVoiceUrl: status.devVoiceUrl,
    };

    // Don't let a status poll override an in-flight bind or a surfaced error.
    if (cur.phase === 'binding' || cur.phase === 'error') return base;

    return { ...base, phase: servingThis ? 'serving' : 'idle' };
  });
}

/** error → idle (user dismiss / retry reset). */
export function dismissError(sid: string): void {
  patchSession(sid, (cur) =>
    cur.phase === 'error' ? { ...cur, phase: 'idle', lastError: null } : cur,
  );
}

// ─── Non-hook accessors (for effect bodies / event handlers) ────────────────────

export function getDevPreview(
  state: DevPreviewStoreShape,
  sid: string | null,
): DevPreviewState | undefined {
  if (!sid) return undefined;
  return state.sessions.get(sid);
}

export function getDevPreviewPhase(
  state: DevPreviewStoreShape,
  sid: string | null,
): DevPreviewPhase {
  if (!sid) return 'idle';
  return state.sessions.get(sid)?.phase ?? 'idle';
}

// ─── Hook selectors — the ONLY supported way to read state in React ─────────────

/** Subscribe to a single session's FSM phase. */
export function useDevPreviewPhase(sid: string | null): DevPreviewPhase {
  return useDevPreviewStore(
    (s) => (sid ? s.sessions.get(sid)?.phase ?? 'idle' : 'idle'),
  );
}

/** Subscribe to a single session's full dev-preview entry (default when absent). */
export function useDevPreviewEntry(sid: string | null): DevPreviewState {
  return useDevPreviewStore(
    useShallow((s) => (sid ? s.sessions.get(sid) ?? defaultDevPreviewState() : defaultDevPreviewState())),
  );
}
